// src/Home.js
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import ListingCard from '../components/ListingCard';
import AddListingModal from '../components/AddListingModal';
import EditListingModal from '../components/EditListingModal';
import { refreshImageUrls, checkNeedsUrlRefresh } from '../services/api';

function Home({ isAdmin, onLogout }) {
  // Full listing array
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Controls Add Listing modal
  const [showModal, setShowModal] = useState(false);
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [listingToEdit, setListingToEdit] = useState(null);

  // We'll use a ref to the "sentinel" div at the bottom for Intersection Observer
  const sentinelRef = useRef(null);

  const navigate = useNavigate();

  // Universal scroll position restoration for Home page
  useEffect(() => {
    const isReturningFromListing = sessionStorage.getItem('lastListingPage') === '/' || 
                                   sessionStorage.getItem('lastListingPage')?.startsWith('/listings/');
    
    if (isReturningFromListing) {
      console.log('🔄 Detected return to home page from listing detail');
      
      // Clear the return flag
      sessionStorage.removeItem('lastListingPage');
      
      // Restore scroll position after content loads
      const scrollTimeout = setTimeout(() => {
        const savedScrollPosition = sessionStorage.getItem('scrollPosition-/');
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position to: ${scrollY}px`);
          
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            window.scrollTo({
              top: scrollY,
              behavior: 'auto'
            });
          });
          
          // Clean up the scroll position after restoring
          sessionStorage.removeItem('scrollPosition-/');
        }
      }, 100);
      
      return () => clearTimeout(scrollTimeout);
    } else {
      // Normal page navigation - scroll to top
      window.scrollTo(0, 0);
    }
  }, [listings.length]);

  // Fetch listings from server
  const fetchListings = async () => {
    try {
      setIsLoading(true);
      const res = await fetch('https://eurocentrumv1.onrender.com/listings');
      const data = await res.json();
      
      // Filter listings to only show those with "aktuálne" tag
      const filteredData = data.filter(listing => 
        listing.tags && listing.tags.includes('aktuálne')
      );
      
      // Refresh image URLs proactively for listings that need it
      const refreshedListings = await Promise.all(
        filteredData.map(async (listing) => {
          if (checkNeedsUrlRefresh(listing)) {
            try {
              console.log(`Refreshing image URLs for listing ${listing._id}`);
              return await refreshImageUrls(listing._id);
            } catch (error) {
              console.error(`Failed to refresh image URLs for listing ${listing._id}:`, error);
              return listing; // Use original listing if refresh fails
            }
          }
          return listing;
        })
      );
      
      setListings(refreshedListings);
    } catch (error) {
      console.error('Error fetching listings', error);
    } finally {
      setIsLoading(false);
    }
  };

  // On mount, fetch all listings
  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line
  }, []);

  // Called after adding a listing
  const handleAddListing = (newListing) => {
    if (newListing.tags && newListing.tags.includes('aktuálne')) {
      console.log("Adding new listing to Home:", newListing);
      
      // FORCE the new listing to the absolute top regardless of any sorting
      setListings(prev => {
        // Create a new array with the new listing at position 0
        const newListings = [newListing];
        
        // Then add all previous listings after it
        prev.forEach(listing => {
          if (listing._id !== newListing._id) {
            newListings.push(listing);
          }
        });
        
        return newListings;
      });
    }
  };

  // Handle edit click for ListingCard
  const handleEditClick = (listing) => {
    setListingToEdit(listing);
    setShowEditModal(true);
  };

  // Enhanced edit modal close handler that preserves scroll position
  const handleEditModalClose = () => {
    console.log('🔄 Admin finished editing listing - preserving scroll position');
    
    // Save current scroll position before closing modal
    const currentPath = window.location.pathname;
    const scrollY = window.scrollY || window.pageYOffset;
    sessionStorage.setItem(`scrollPosition-${currentPath}`, scrollY.toString());
    
    // Set flag to indicate we should restore position after refresh
    sessionStorage.setItem('preserveScrollAfterEdit', 'true');
    
    console.log(`💾 Saved scroll position for admin edit: ${scrollY}px`);
    
    setShowEditModal(false);
    setListingToEdit(null);
  };

  // Enhanced refresh function that restores scroll position after admin edits
  const handleRefreshAfterEdit = async () => {
    await fetchListings();
    
    // Check if we need to restore scroll position after edit
    const shouldPreserveScroll = sessionStorage.getItem('preserveScrollAfterEdit');
    
    if (shouldPreserveScroll) {
      console.log('🔄 Restoring scroll position after admin edit');
      
      // Remove the flag
      sessionStorage.removeItem('preserveScrollAfterEdit');
      
      // Restore scroll position after content loads
      setTimeout(() => {
        const currentPath = window.location.pathname;
        const savedScrollPosition = sessionStorage.getItem(`scrollPosition-${currentPath}`);
        
        if (savedScrollPosition) {
          const scrollY = parseInt(savedScrollPosition, 10);
          console.log(`📍 Restoring scroll position after edit: ${scrollY}px`);
          
          window.scrollTo({
            top: scrollY,
            behavior: 'auto'
          });
          
          // Clean up
          sessionStorage.removeItem(`scrollPosition-${currentPath}`);
        }
      }, 100);
    }
  };

  // Handle tag click from ListingCard
  const handleTagClick = (tag) => {
    if (tag === 'auto') {
      navigate('/auta');
    } else if (tag === 'byt' || tag === 'dom') {
      navigate('/nehnutelnosti');
    } else if (tag === 'neplatiči') {
      navigate('/neplatici');
    } else if (tag !== 'aktuálne') {
      // For any other tags, direct to appropriate category
      if (['predaj', 'prenájom'].includes(tag)) {
        navigate('/nehnutelnosti');
      }
    }
  };
  
  // Items actually displayed (limited by visibleCount)
  // const displayedListings = listings.slice(0, visibleCount); // REMOVE

  // Intersection Observer: whenever sentinelRef is in view, we load 6 more
  // useEffect(() => { ... }); // REMOVE

  // Animation variants for each item
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // const loadMoreListings = () => { ... } // REMOVE

  return (
    <div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <HeroSection isAdmin={isAdmin} onLogout={onLogout} />

      <div style={styles.container}>
        <header style={styles.header}>
          {/* Left side: heading */}
          <h2 style={styles.heading}>Aktuálne ponuky</h2>
        </header>

        <div style={styles.listingsContainer}>
          {isLoading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner}></div>
              <p>Načítavam ponuky...</p>
            </div>
          ) : (
            <AnimatePresence>
              {listings.map((listing) => (
                <motion.div
                  key={listing._id}
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0 }}
                  variants={itemVariants}
                  transition={{ duration: 0.3 }}
                >
                  <ListingCard
                    listing={listing}
                    isAdmin={isAdmin}
                    onRefresh={fetchListings}
                    onTagClick={handleTagClick}
                    onEditClick={handleEditClick}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Show a message when no listings match the selected filters */}
        {!isLoading && listings.length === 0 && (
          <p style={styles.emptyMessage}>Žiadne aktuálne záznamy.</p>
        )}

        {/* The sentinel div for Intersection Observer */}
        {/* <div ref={sentinelRef} style={{ height: '20px' }} /> */}

        {showModal && isAdmin && (
          <AddListingModal
            listingToEdit={null}
            onClose={() => setShowModal(false)}
            onRefresh={fetchListings}
            onAddListing={handleAddListing}
          />
        )}

        {/* Edit Listing Modal */}
        {showEditModal && isAdmin && (
          <EditListingModal
            listingToEdit={listingToEdit}
            onClose={handleEditModalClose}
            onRefresh={handleRefreshAfterEdit}
          />
        )}
      </div>

      {/* =============================== */}
      {/* TERMS SECTION WITH OVERLAID GRADIENT */}
      {/* =============================== */}
      <section style={styles.termsSection}>
        <div style={styles.termsColumn}>
          <h2 style={styles.termsHeading}>PODMIENKY PRI PRENÁJME BYTOV</h2>
          <ul style={styles.termsList}>
            <li>– nájom pri garsónke, 1-izbovom a 2-izbovom byte je 250 Eur</li>
            <li>– nájom pri 3-izbovom byte je 400 Eur</li>
            <li>– kaucia sa platí jednorázovo 500 Eur alebo na mesačné splátky 1 500 Eur</li>
            <li>– každý nájomca má možnosť po podpise nájomnej zmluvy si nahlásiť trvalý pobyt...</li>
            {/* Add more list items as needed */}
          </ul>
        </div>
        <div style={styles.termsColumn}>
          <h2 style={styles.termsHeading}>PODMIENKY PRI PRENÁJME ÁUT</h2>
          <ul style={styles.termsList}>
            <li>– minimálny vek 18 rokov</li>
            <li>– vodičský preukaz</li>
            <li>– zábezpeka sa u nás neplatí žiadna</li>
            <li>– autá máme k dispozícii NONSTOP</li>
            {/* Add more list items as needed */}
          </ul>
        </div>
      </section>
    </div>
  );
}

export default Home;

const styles = {
  container: {
    fontFamily: "'Inter', Helvetica, Arial, sans-serif",
    margin: '0 auto',
    maxWidth: '1200px',
    padding: '3rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '1rem',
    width: '100%',
    textAlign: 'center',
  },
  heading: {
    margin: 0,
    fontSize: '2.5rem',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addButton: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    borderRadius: '0.5rem',
  },
  listingsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    justifyContent: 'center',
    width: '100%',
  },
  emptyMessage: {
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#666',
    margin: '3rem 0',
  },
  bottomButtonContainer: {
    marginTop: '2rem',
    textAlign: 'center',
  },
  allPostsButton: {
    backgroundColor: 'rgb(44, 105, 160)',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '0.8rem 1.5rem',
    fontSize: '1.1rem',
    cursor: 'pointer',
  },
  termsSection: {
    background: `
      linear-gradient(
        45deg,
        rgb(44, 105, 160) 0%,
        rgb(28, 63, 115) 100%
      )
    `,
    color: '#fff',
    padding: '4rem 2rem',
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '3rem',
  },
  termsColumn: {
    flexBasis: '45%',
    minWidth: '300px',
  },
  termsHeading: {
    fontSize: '1.7rem',
    fontWeight: '700',
    marginBottom: '1.5rem',
  },
  termsList: {
    listStyleType: 'none',
    padding: 0,
    fontSize: '1rem',
    lineHeight: '1.8',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderTop: '4px solid rgb(44, 105, 160)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};
